// Der Abschnitt "Beziehungen" des Kontaktdetails (ST-REL-001; ADR-0061
// Ziffer 17), im Muster Pflegeabschnitt in seinem eingebetteten Format:
// Laden, EINE Statusregion, Bestaetigung, Wiederholung und Beenden kommen aus
// dem Muster. Fachlich bleiben hier die Lesart, die Suche nach dem anderen
// Kontakt und "Funktion ändern".

import { beiEscapeSchliessen, PlusSymbol, VerknuepfenSymbol } from './plus-symbol.tsx';
import { useEffect, useRef, useState, type FormEvent, type ReactElement } from 'react';
import {
  Body1,
  Button,
  Caption1,
  Dropdown,
  Field,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Option,
  Radio,
  RadioGroup,
  Spinner,
  Subtitle1,
  Subtitle2,
  Tooltip,
} from '@fluentui/react-components';
import type { PlainMessage } from '../../access.ts';
import { MehrKnopf } from '../../shell/mehr-knopf.tsx';
import { keepsRequest } from '../../client-request.ts';
import type { CommandOutcome } from '../../command-client.ts';
import { kindLabel, partyHref, searchParties, searchTermProblem, type PartySummary } from '../../parties.ts';
import {
  changeRelationshipRole,
  changeRoleButtonLabel,
  createdAnnouncement,
  createRelationship,
  CREATE_UNREACHABLE_MESSAGE,
  DUPLICATE_WITH_ROLE_MESSAGE,
  eligibleCounterparts,
  eligibleHitsStatement,
  EMPTY_STATEMENT,
  endedAnnouncement,
  endRetryLabel,
  ENDED_HEADLINE,
  endRelationship,
  END_UNREACHABLE_MESSAGE,
  fetchRelationshipPage,
  fetchRelationships,
  fieldMessageFor,
  groupRelationships,
  HAS_MORE_NOTICE,
  isOrganizationView,
  LOAD_UNREACHABLE_MESSAGE,
  NO_CONTACT_PERSONS_STATEMENT,
  noEligibleHitsStatement,
  optionKey,
  PARTY_GONE_MESSAGE,
  relationshipLine,
  ROLE_CODES,
  roleChangedAnnouncement,
  roleLabel,
  ROLE_UNREACHABLE_MESSAGE,
  ROLE_UNREACHABLE_NOT_RELOADED_MESSAGE,
  SEARCH_UNREACHABLE_MESSAGE,
  settleEndAfterAbort,
  formatSinceMonth,
  sinceText,
  typeCarriesRole,
  type CreateRelationshipResult,
  type EndRelationshipResult,
  type RelationshipOption,
  type RelationshipView,
  CONTACT_PERSON_TYPES,
} from '../../relationships.ts';
import { presetWorksFor, type WorksForChoice } from '../../quick-capture.ts';
import { Pflegeabschnitt, Zeilenaktionen, type PflegeabschnittTexte } from '../../shell/pflegeabschnitt.tsx';
import { AppLink, navigate } from '../../shell/router.tsx';
import { Vorgang } from '../../shell/zustand.tsx';
import { useScreenStyles } from '../../shell/screen-styles.ts';
import { useMaintainedSection, type SectionLoadResult } from '../../shell/use-maintained-section.ts';
import { useKontaktStyles } from './stile.ts';
import {
  RELATIONSHIP_HITS_ID,
  RELATIONSHIP_READING_INPUT_ID,
  RELATIONSHIP_SEARCH_INPUT_ID,
  RELATIONSHIP_STATUS_ID,
} from './ids.ts';
import type { KontaktScreenProps } from './typen.ts';
import { NebenKnopf } from '../../shell/nebenknopf.tsx';

// AUFGABE IN NUTZERSPRACHE: "Ich moechte festhalten, dass Maria Beispiel fuer
// den Verlag Nord arbeitet." Zwei Schritte ab dem geoeffneten Kontakt: die
// Lesart in Worten waehlen, den anderen Kontakt suchen und waehlen -- dann
// festhalten. Beginn, Sichtbarkeit und Kontext setzt LIAISON (ADR-0061
// Ziffer 1 und 10); eine Richtung "A/B" gibt es auf dem Bildschirm nicht.
//
// BEENDEN OHNE DIALOG (ADR-0061 Ziffer 17): es loescht nichts, und der
// Rueckweg -- erneut festhalten -- liegt im selben Abschnitt. Die Bestaetigung
// sagt das ausdruecklich.

interface Bestand {
  readonly relationships: readonly RelationshipView[];
  readonly hasMore: boolean;
  /** Der Cursor der zweiten Seite; fehlt er bei hasMore, bleibt nur der Hinweis. */
  readonly nextCursor?: string;
  readonly options: readonly RelationshipOption[];
}

/** Der Stand von "Weitere laden"; das Ergebnis steht AM KNOPF, nicht oben im Abschnitt. */
type Nachladen =
  | { readonly art: 'ruht' }
  | { readonly art: 'laedt' }
  | { readonly art: 'fertig'; readonly neu: number }
  | { readonly art: 'fehler'; readonly meldung: PlainMessage };

/** Nachgeladene Seiten: Kanten hinter den ersten 50 (Cursor der Route, #533). */
interface Weitere {
  readonly kanten: readonly RelationshipView[];
  readonly cursor: string | undefined;
  readonly hasMore: boolean;
}

const TEXTE: PflegeabschnittTexte = {
  laden: 'Beziehungen werden geladen',
  feldfehlerTitel: 'Die Beziehung wurde nicht festgehalten.',
  feldfehlerText: 'Das markierte Feld sagt, was zu ändern ist.',
  erneutLaden: 'Beziehungen erneut laden',
  unbekanntErklaerung: 'Er wurde entfernt, oder du kannst ihn nicht mehr öffnen.',
};

/**
 * Die Sendung, fuer die die gehaltene Kennung gilt -- dieselben Felder wie
 * der Fingerprint (ADR-0061 Ziffer 8); die Funktion nur, wenn es eine gibt.
 */
function sendungOf(option: RelationshipOption, gegenpartei: string, funktion: string | undefined): Readonly<Record<string, string>> {
  return {
    relationshipTypeCode: option.relationshipTypeCode,
    contactSide: option.contactSide,
    counterpartPartyId: gegenpartei,
    ...(funktion === undefined ? {} : { roleCode: funktion }),
  };
}

/**
 * Der Ausgang einer Ablehnung in der Sprache des Musters: eine Stoerung haelt
 * Kennung und Stand ("Erneut versuchen"), alles andere laedt still nach. Der
 * Grund reist mit, denn an ihm erkennt das Muster den ueberholten ersten
 * Versuch.
 */
function ablehnung<Applied>(ergebnis: { readonly outcome: 'rejected'; readonly message: PlainMessage; readonly reasonCode?: string | undefined }): CommandOutcome<Applied> {
  if (keepsRequest(ergebnis)) return { outcome: 'rejected', message: ergebnis.message };
  return ergebnis.reasonCode === undefined
    ? { outcome: 'conflict', message: ergebnis.message }
    : { outcome: 'conflict', message: ergebnis.message, reasonCode: ergebnis.reasonCode };
}

function endeAlsBefehl(ergebnis: EndRelationshipResult): CommandOutcome<{ readonly confirmedAfterAbort: boolean }> {
  if (ergebnis.outcome === 'ended') return { outcome: 'applied', confirmedAfterAbort: ergebnis.confirmedAfterAbort === true };
  // Der Kontakt selbst ist weg: das Nachladen zeigt es (Muster).
  if (ergebnis.outcome === 'not-found') return { outcome: 'not-found', message: PARTY_GONE_MESSAGE };
  return ablehnung(ergebnis);
}

/** "Keine Funktion" in der Auswahl; kein Code der Werteliste sieht so aus. */
const OHNE_FUNKTION = '-';
const RELATIONSHIP_ROLE_INPUT_ID = 'kontakt-beziehung-funktion';
const TITEL: Readonly<Record<BeziehungsTeil, string>> = { alle: 'Beziehungen', ansprechpartner: 'Ansprechpartner:innen', weitere: 'Beziehungen' };
/** Ansprechpartner, die ohne Aufklappen sichtbar sind (DESIGN-P1 #518). */
const SICHTBARE_ANSPRECHPARTNER = 5;
const NO_FURTHER_RELATIONSHIPS = 'Für diese Organisation ist keine weitere Beziehung festgehalten.';
/** Der Oeffner des Anlegeformulars (Slice 4c). */
const RELATIONSHIP_OPENER_ID = 'beziehung-hinzufuegen';
const funktionEingabeId = (relationshipId: string): string => `kontakt-beziehung-funktion-neu-${relationshipId}`;
const funktionKnopfId = (relationshipId: string): string => `kontakt-beziehung-funktion-aendern-${relationshipId}`;

/** Die Art kurz neben dem Namen, wo sie mehr sagt als die Funktion (Glossar 13.10.). */
const ART_KURZ: Readonly<Record<string, string>> = { contributes_to: 'freie Mitarbeit', leads: 'Leitung' };

/**
 * Die Angaben einer Ansprechpartner-Zeile: die Art, wo sie zaehlt (freie Mitarbeit,
 * Leitung), dann die Funktion -- ohne Funktion bei einer Anstellung „arbeitet für“
 * --, dann „seit MM/JJJJ“. Die Lesart aus Sicht der Organisation („beschäftigt“)
 * steht nicht mehr da (Glossar, DESIGN-P3 #553).
 */
function ansprechZeilentext(beziehung: RelationshipView, funktion: string | undefined): string {
  const art = ART_KURZ[beziehung.relationshipTypeCode];
  const rolle = funktion ?? (art === undefined ? (beziehung.relationshipTypeCode === 'works_for' ? 'arbeitet für' : beziehung.reading) : undefined);
  return [art, rolle, sinceText(formatSinceMonth(beziehung.validFrom))].filter((teil) => teil !== undefined).join(' · ');
}

/**
 * Meldet die Zahl der Ansprechpartner an die Ueberschrift. Die Liste entsteht im
 * Pflegeabschnitt; die Ueberschrift steht davor -- ein Effekt statt eines
 * Zustandswechsels waehrend des Zeichnens.
 */
function AnzahlMelden({ anzahl, melden }: { readonly anzahl: number | undefined; readonly melden: (anzahl: number | undefined) => void }): null {
  // Verschwindet die Liste (Neuladen, Stoerung), verschwindet auch die Zahl.
  useEffect(() => {
    melden(anzahl);
    return () => melden(undefined);
  }, [anzahl, melden]);
  return null;
}

/** Saetze hintereinander, jeder mit Schlusszeichen -- sonst liest sich „gestört Versuche es“. */
function satzfolge(saetze: readonly string[]): string {
  return saetze
    .map((satz) => satz.trim())
    .filter((satz) => satz !== '')
    .map((satz) => (/[.!?]$/.test(satz) ? satz : `${satz}.`))
    .join(' ');
}

function MehrSymbol(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden="true" focusable="false" fill="currentColor">
      <circle cx="5.5" cy="12" r="1.4" />
      <circle cx="12" cy="12" r="1.4" />
      <circle cx="18.5" cy="12" r="1.4" />
    </svg>
  );
}

function fokusAufTreffer(trefferId: string): void {
  const gewaehlt = document.querySelector<HTMLInputElement>(`#${trefferId} input:checked`);
  (gewaehlt ?? document.querySelector<HTMLInputElement>(`#${trefferId} input`))?.focus();
}

/**
 * Welcher Teil der Beziehungen dieser Abschnitt zeigt (Slice 6b, DESIGN A′): an
 * einer Organisation stehen die Ansprechpersonen MIT ihren Aktionen im eigenen
 * Abschnitt "Ansprechpartner" ueber dem Verlauf, links bleiben die weiteren
 * Beziehungen. Jeder Teil hat eigene Kennungen, damit beide auf einer Seite
 * stehen koennen; "alle" ist der bisherige Abschnitt (Personen).
 */
export type BeziehungsTeil = 'alle' | 'ansprechpartner' | 'weitere';

function kennungenFuer(teil: BeziehungsTeil) {
  const zusatz = teil === 'ansprechpartner' ? '-ansprechpartner' : '';
  return {
    ueberschrift: teil === 'ansprechpartner' ? 'ansprechpartner' : teil === 'weitere' ? 'weitere-beziehungen' : 'beziehungen',
    status: `${RELATIONSHIP_STATUS_ID}${zusatz}`,
    lesart: `${RELATIONSHIP_READING_INPUT_ID}${zusatz}`,
    suche: `${RELATIONSHIP_SEARCH_INPUT_ID}${zusatz}`,
    treffer: `${RELATIONSHIP_HITS_ID}${zusatz}`,
    funktion: `${RELATIONSHIP_ROLE_INPUT_ID}${zusatz}`,
    oeffner: `${RELATIONSHIP_OPENER_ID}${zusatz}`,
  };
}

/**
 * Der Abschnitt "Beziehungen".
 *
 * In die EINE Statusregion des Musters kommen nur ERGEBNISSE einer Handlung --
 * Festhalten, Beenden, Funktion aendern und der Ausgang einer Suche. Laden,
 * Leerzustand, Liste und der Hinweis auf mehr als 50 stehen ausserhalb.
 */

export function Beziehungen({
  apiBaseUrl,
  accessToken,
  onReauthenticate,
  partyId,
  eingebettet = false,
  nurLesen = false,
  onNichtBeschreibbar,
  teil = 'alle',
  organisation,
}: KontaktScreenProps & {
  readonly teil?: BeziehungsTeil;
  /** Die Organisation dieses Kontakts, fuer "Ansprechpartner anlegen" (Vorbelegung der Schnellerfassung). */
  readonly organisation?: WorksForChoice;
  /**
   * Im aufklappbaren Bereich traegt der Accordion-Kopf (h2, id
   * "beziehungen") die Ueberschrift; der Abschnitt setzt dann keine eigene,
   * und aria-labelledby zeigt auf den Kopf.
   */
  readonly eingebettet?: boolean;
  /** Der Kontakt ist nicht beschreibbar: dieser Abschnitt zeigt nur noch an (Server: 409 party_not_writable). */
  readonly nurLesen?: boolean;
  /** Der Server lehnte mit 409 party_not_writable ab: die Seite liest den Kontakt neu, damit die Leiste den Zustand zeigt. */
  readonly onNichtBeschreibbar?: () => void;
}): ReactElement {
  const styles = useScreenStyles();
  const own = useKontaktStyles();
  const ids = kennungenFuer(teil);

  const abschnitt = useMaintainedSection<Bestand>({
    accessToken,
    onReauthenticate,
    sectionKey: partyId,
    createMessages: { submitFailed: CREATE_UNREACHABLE_MESSAGE },
    reloadedAnnouncement: 'Die Beziehungen zeigen jetzt den aktuellen Stand.',
    fetchSection: async (token): Promise<SectionLoadResult<Bestand>> => {
      try {
        const stand = await fetchRelationships({ baseUrl: apiBaseUrl, accessToken: token, partyId });
        if (stand.outcome === 'not-found') {
          // Derselbe Rueckweg wie in den Nachbarabschnitten des Kontaktdetails.
          onReauthenticate();
          return stand;
        }
        if (stand.outcome === 'rejected') return stand;
        return {
          outcome: 'loaded',
          data: { relationships: stand.relationships, hasMore: stand.hasMore, ...(stand.nextCursor === undefined ? {} : { nextCursor: stand.nextCursor }), options: stand.options },
        };
      } catch {
        return { outcome: 'rejected', message: LOAD_UNREACHABLE_MESSAGE };
      }
    },
  });
  const { busy, data } = abschnitt;
  // Je Teil nur die passenden Lesarten: Ansprechpartner ordnen Menschen zu, die
  // weiteren Beziehungen alles andere.
  const lesarten = (data?.options ?? []).filter((option) =>
    teil === 'alle' ? true : CONTACT_PERSON_TYPES.includes(option.relationshipTypeCode) === (teil === 'ansprechpartner'),
  );

  /** Der Ausgang der letzten Suche, in Worten -- ebenfalls Ergebnis einer Handlung. */
  const [suchsatz, setSuchsatz] = useState<string | undefined>(undefined);
  /**
   * Die Beziehungen, deren Beenden abbrach. Eine Wiederholung -- ueber
   * "Erneut versuchen" ODER den Zeilenknopf -- liest "bereits beendet" dann
   * als Erfolg (settleEndAfterAbort).
   */
  const endeAbgebrochenRef = useRef(new Set<string>());

  // Das Formular.
  /** Das Anlegeformular ist zu, bis jemand "Beziehung hinzufügen" waehlt (Slice 4c; kein gemerkter Zustand). */
  const [formOffen, setFormOffen] = useState(false);
  const [lesart, setLesart] = useState<string | undefined>(undefined);
  const [begriff, setBegriff] = useState('');
  const [treffer, setTreffer] = useState<readonly PartySummary[] | undefined>(undefined);
  const [trefferMehr, setTrefferMehr] = useState(false);
  const [sucht, setSucht] = useState(false);
  const [gegenpartei, setGegenpartei] = useState<string | undefined>(undefined);
  const [lesartFehler, setLesartFehler] = useState<string | undefined>(undefined);
  const [sucheFehler, setSucheFehler] = useState<string | undefined>(undefined);
  const [gegenparteiFehler, setGegenparteiFehler] = useState<string | undefined>(undefined);
  /** Die Funktion beim Festhalten -- optional, nichts ist vorbelegt. */
  const [funktion, setFunktion] = useState<string | undefined>(undefined);

  // "Funktion ändern" in der Zeile: hoechstens eine Zeile ist offen.
  const [inAenderung, setInAenderung] = useState<string | undefined>(undefined);
  const [neueFunktion, setNeueFunktion] = useState<string | undefined>(undefined);
  const [aenderungsfehler, setAenderungsfehler] = useState<string | undefined>(undefined);

  const sucheLaufRef = useRef(0);

  // Ansprechpartner: hoechstens fuenf sichtbar, damit die Hauptaktion im ersten
  // Bildschirm bleibt (DESIGN-P1 #518); "Alle N anzeigen" klappt im Abschnitt auf
  // und setzt den Fokus auf die sechste Zeile.
  const [alleZeigen, setAlleZeigen] = useState(false);
  /** Die Zahl neben "Ansprechpartner": nur bei vollstaendiger Liste (G1, Dirigent 13.10.). */
  const [anzahl, setAnzahl] = useState<number | undefined>(undefined);
  const [fokusSechste, setFokusSechste] = useState(false);
  /**
   * "Weitere laden" (Cursor der Route): die Seiten hinter den ersten 50. Sie
   * gelten fuer den geladenen Stand; laedt der Abschnitt neu (nach einer
   * Handlung), beginnt die Liste wieder mit der ersten Seite.
   */
  const [weitere, setWeitere] = useState<Weitere | undefined>(undefined);
  const [nachladen, setNachladen] = useState<Nachladen>({ art: 'ruht' });
  const nachladeLauf = useRef(0);
  const nachladeStatusRef = useRef<HTMLDivElement | null>(null);
  const [fokusAuf, setFokusAuf] = useState<{ readonly id: string } | 'status' | undefined>(undefined);
  useEffect(() => {
    nachladeLauf.current += 1;
    setWeitere(undefined);
    setNachladen({ art: 'ruht' });
  }, [data]);
  // Nach "Weitere laden" steht der Fokus auf dem ersten neuen Eintrag; kam keiner
  // und verschwindet der Knopf, auf der Statusregion am Knopf -- nie auf body.
  useEffect(() => {
    if (fokusAuf === undefined) return;
    setFokusAuf(undefined);
    if (fokusAuf === 'status') {
      nachladeStatusRef.current?.focus();
      return;
    }
    document.querySelector<HTMLElement>(`[data-beziehung="${CSS.escape(fokusAuf.id)}"] a`)?.focus();
  }, [fokusAuf]);
  const listeRef = useRef<HTMLUListElement | null>(null);
  useEffect(() => {
    if (!fokusSechste) return;
    listeRef.current?.querySelector<HTMLAnchorElement>(`li:nth-child(${SICHTBARE_ANSPRECHPARTNER + 1}) a`)?.focus();
    setFokusSechste(false);
  }, [fokusSechste]);

  const gewaehlteLesart = lesarten.find((option) => optionKey(option) === lesart);
  const passende = treffer === undefined ? [] : eligibleCounterparts(treffer, gewaehlteLesart, partyId);
  const gewaehlterTreffer = passende.find((eintrag) => eintrag.partyId === gegenpartei);

  const formularLeeren = (): void => {
    setLesart(undefined);
    setBegriff('');
    setTreffer(undefined);
    setTrefferMehr(false);
    setGegenpartei(undefined);
    setFunktion(undefined);
    setLesartFehler(undefined);
    setSucheFehler(undefined);
    setGegenparteiFehler(undefined);
  };

  // --- Schritt 2: der andere Kontakt ueber die bestehende Suche (ADR-0049) --
  const suchen = async (): Promise<void> => {
    const problem = searchTermProblem(begriff);
    if (problem !== undefined) {
      // Die freundlichere Kontrolle zuerst; der Fokus bleibt im Feld, dort
      // liest der Screenreader die Meldung ueber aria-describedby vor.
      setSucheFehler(problem);
      document.getElementById(ids.suche)?.focus();
      return;
    }
    const lauf = sucheLaufRef.current + 1;
    sucheLaufRef.current = lauf;
    const gueltig = (): boolean => sucheLaufRef.current === lauf;
    setSucheFehler(undefined);
    setGegenparteiFehler(undefined);
    abschnitt.clearOutcome();
    setSuchsatz(undefined);
    setSucht(true);
    try {
      const token = await accessToken();
      if (!gueltig()) return;
      if (token === undefined) {
        setSucht(false);
        onReauthenticate();
        return;
      }
      const ergebnis = await searchParties({ baseUrl: apiBaseUrl, accessToken: token, term: begriff.trim() });
      if (!gueltig()) return;
      setSucht(false);
      if (ergebnis.outcome === 'rejected') {
        setTreffer(undefined);
        setGegenpartei(undefined);
        abschnitt.showMessage(ergebnis.message);
        return;
      }
      const passendNeu = eligibleCounterparts(ergebnis.parties, gewaehlteLesart, partyId);
      setTreffer(ergebnis.parties);
      setTrefferMehr(ergebnis.hasMore);
      // Keine Vorauswahl, auch nicht bei einem einzigen Treffer: wer mit wem
      // in Beziehung steht, bestaetigt die Nutzerin selbst.
      setGegenpartei(undefined);
      // Der Ausgang geht in die Statusregion und wird angesagt; der Fokus
      // bleibt im Suchfeld, damit man den Begriff weiter verfeinern kann.
      setSuchsatz(
        passendNeu.length === 0
          ? noEligibleHitsStatement(ergebnis.parties.filter((eintrag) => eintrag.partyId !== partyId).length, gewaehlteLesart?.counterpartKind ?? null)
          : eligibleHitsStatement(passendNeu.length, ergebnis.hasMore),
      );
    } catch {
      if (!gueltig()) return;
      setSucht(false);
      abschnitt.showMessage(SEARCH_UNREACHABLE_MESSAGE);
    }
  };

  // Ein Suchsatz ist das Ergebnis der Suche, nicht des naechsten Befehls.
  const befehlBeginnt = (): void => setSuchsatz(undefined);

  // --- Festhalten ------------------------------------------------------------
  const festhaltenAlsBefehl = (ergebnis: CreateRelationshipResult, mitFunktion: string | undefined): CommandOutcome<object> => {
    if (ergebnis.outcome === 'created') return { outcome: 'applied' };
    if (ergebnis.outcome === 'not-found') return { outcome: 'not-found', message: PARTY_GONE_MESSAGE };
    if (ergebnis.outcome === 'invalid') {
      // Die Auswahl passt nicht mehr zum Server: am Feld sagen; das Muster
      // laedt still nach (refreshOnInvalid) und fuehrt den Fokus ins Feld.
      const lesartFalsch = ergebnis.errors.some((fehler) => fehler.fieldPath === 'relationshipTypeCode' || fehler.fieldPath === 'contactSide');
      if (lesartFalsch) {
        setLesart(undefined);
        setLesartFehler(fieldMessageFor('relationshipTypeCode'));
      } else {
        setGegenpartei(undefined);
        setGegenparteiFehler(fieldMessageFor('counterpartPartyId'));
      }
      return { outcome: 'invalid', errors: ergebnis.errors };
    }
    if (ergebnis.reasonCode === 'counterpart_not_found' || ergebnis.reasonCode === 'counterpart_not_writable') {
      setTreffer(undefined);
      setGegenpartei(undefined);
    }
    if (ergebnis.reasonCode === 'relationship_duplicate') {
      formularLeeren();
      // Mit gewaehlter Funktion ist die Dublette meist der Versuch, sie
      // nachzutragen -- der Satz zeigt dann den Weg in die Zeile.
      if (mitFunktion !== undefined) return { outcome: 'conflict', message: DUPLICATE_WITH_ROLE_MESSAGE };
    }
    if (ergebnis.reasonCode === 'party_not_writable') onNichtBeschreibbar?.();
    return ablehnung(ergebnis);
  };

  const festhalten = (): void => {
    if (gewaehlteLesart === undefined) {
      setLesartFehler('Wähle zuerst aus, wie die beiden Kontakte zusammenhängen.');
      document.getElementById(ids.lesart)?.focus();
      return;
    }
    if (gewaehlterTreffer === undefined) {
      if (passende.length === 0) {
        setSucheFehler('Suche zuerst den anderen Kontakt.');
        document.getElementById(ids.suche)?.focus();
      } else {
        setGegenparteiFehler('Wähle einen der Treffer aus.');
        fokusAufTreffer(ids.treffer);
      }
      return;
    }
    const option = gewaehlteLesart;
    const ziel = gewaehlterTreffer;
    const mitFunktion = typeCarriesRole(option.relationshipTypeCode) ? funktion : undefined;
    // Dieselbe Sendung traegt dieselbe Kennung (ADR-0060); das Muster haelt
    // sie, bis eine Wiederholung nichts mehr aendern kann.
    void abschnitt.create({
      input: sendungOf(option, ziel.partyId, mitFunktion),
      refreshOnInvalid: true,
      run: async (token, clientRequestId) => {
        befehlBeginnt();
        const ergebnis = await createRelationship({
          baseUrl: apiBaseUrl,
          accessToken: token,
          partyId,
          option,
          counterpartPartyId: ziel.partyId,
          roleCode: mitFunktion,
          clientRequestId,
        });
        return festhaltenAlsBefehl(ergebnis, mitFunktion);
      },
      confirm: () => createdAnnouncement(option.reading, ziel.displayName),
      // Erfolg schliesst das Formular; eine Ablehnung laesst es mit den Werten offen.
      clearInput: () => {
        formularLeeren();
        setFormOffen(false);
      },
    });
  };

  // Welcher Oeffner das Formular geoeffnet hat: das Menue "…" der Kopfzeile oder,
  // an einer Organisation ohne Ansprechpartner, der Knopf unter dem Leersatz.
  const oeffnerRef = useRef(ids.oeffner);
  const zuordnenOeffnen = (oeffnerId: string): void => {
    oeffnerRef.current = oeffnerId;
    setFormOffen(true);
    window.setTimeout(() => document.getElementById(ids.lesart)?.focus(), 0);
  };

  // Abbrechen und Escape: eine laufende Suche entwerten, verwerfen, Fokus an den Oeffner.
  const formSchliessen = (): void => {
    // Eine laufende Suche verwerfen: ihre spaete Antwort darf
    // weder Treffer noch einen angesagten Suchsatz in ein
    // geschlossenes Formular tragen (Experience-Review 4c, P2-1).
    sucheLaufRef.current += 1;
    setSucht(false);
    setSuchsatz(undefined);
    formularLeeren();
    setFormOffen(false);
    const zurueck = oeffnerRef.current;
    window.setTimeout(() => (document.getElementById(zurueck) ?? document.getElementById(ids.oeffner))?.focus(), 0);
  };

  const absenden = (event: FormEvent): void => {
    event.preventDefault();
    festhalten();
  };

  // --- Beenden, ohne Dialog ---------------------------------------------------
  const beenden = (beziehung: RelationshipView): void => {
    void abschnitt.command({
      run: async (token) => {
        befehlBeginnt();
        const nachAbbruch = endeAbgebrochenRef.current.has(beziehung.relationshipId);
        let ergebnis;
        try {
          ergebnis = await endRelationship({ baseUrl: apiBaseUrl, accessToken: token, partyId, relationship: beziehung });
        } catch (fehler) {
          // Ob das Ende geschrieben ist, weiss hier niemand. Die Zeile bleibt
          // stehen, "Erneut versuchen" sendet dasselbe Beenden mit demselben
          // Stand; ein schon gespeichertes Ende gilt dann als Erfolg.
          endeAbgebrochenRef.current.add(beziehung.relationshipId);
          throw fehler;
        }
        // Es gab einen Ausgang: die naechste Sendung ist keine Wiederholung mehr.
        endeAbgebrochenRef.current.delete(beziehung.relationshipId);
        return endeAlsBefehl(settleEndAfterAbort(ergebnis, nachAbbruch));
      },
      confirmTitle: ENDED_HEADLINE,
      confirm: (ende) => endedAnnouncement(beziehung, ende.confirmedAfterAbort),
      unreachable: END_UNREACHABLE_MESSAGE,
      retryAfterAbort: true,
      retryLabel: endRetryLabel(beziehung),
    });
  };

  // --- Funktion aendern: beenden und neu anlegen in EINER Transaktion (F2) ---
  const aenderungOeffnen = (beziehung: RelationshipView): void => {
    setInAenderung(beziehung.relationshipId);
    setNeueFunktion(undefined);
    setAenderungsfehler(undefined);
    window.setTimeout(() => document.getElementById(funktionEingabeId(beziehung.relationshipId))?.focus(), 0);
  };

  const aenderungSchliessen = (zurueck?: RelationshipView): void => {
    setInAenderung(undefined);
    setNeueFunktion(undefined);
    setAenderungsfehler(undefined);
    if (zurueck !== undefined) {
      window.setTimeout(() => document.getElementById(funktionKnopfId(zurueck.relationshipId))?.focus(), 0);
    }
  };

  const funktionAendern = (beziehung: RelationshipView, ziel: string | null): void => {
    void abschnitt.command({
      run: async (token): Promise<CommandOutcome<object>> => {
        befehlBeginnt();
        let ergebnis;
        try {
          // Den Stand schickt die Zeile mit; ein abweichender ist ein 409.
          ergebnis = await changeRelationshipRole({ baseUrl: apiBaseUrl, accessToken: token, partyId, relationship: beziehung, roleCode: ziel });
        } catch (fehler) {
          // Ob der Wechsel geschrieben ist, weiss hier niemand. Ein Wiederholen
          // mit dem alten Stand liefe ins Leere (die Kante hat danach eine neue
          // Kennung); der Hook laedt nach (reloadAfterAbort) und sagt, ob das gelang.
          aenderungSchliessen();
          throw fehler;
        }
        if (ergebnis.outcome === 'changed') return { outcome: 'applied' };
        if (ergebnis.message.retryable === true) {
          // Eine Stoerung des Dienstes: nichts ist geschrieben, der Stand gilt
          // weiter. Die Zeile bleibt mit der Wahl offen; "Funktion speichern"
          // ist der erneute Versuch.
          return { outcome: 'rejected', message: ergebnis.message };
        }
        // Nach einem Konflikt ist der Stand der Zeile veraltet.
        aenderungSchliessen();
        return { outcome: 'conflict', message: ergebnis.message };
      },
      confirm: () => roleChangedAnnouncement(beziehung, ziel),
      // Nach dem Erfolg traegt die Kante eine neue Kennung; das Muster laedt nach.
      onApplied: () => aenderungSchliessen(),
      reloadAfterAbort: { reloaded: ROLE_UNREACHABLE_MESSAGE, notReloaded: ROLE_UNREACHABLE_NOT_RELOADED_MESSAGE },
    });
  };

  const aendernSenden = (event: FormEvent, beziehung: RelationshipView): void => {
    event.preventDefault();
    if (neueFunktion === undefined) {
      setAenderungsfehler('Wähle zuerst die neue Funktion aus.');
      document.getElementById(funktionEingabeId(beziehung.relationshipId))?.focus();
      return;
    }
    funktionAendern(beziehung, neueFunktion === OHNE_FUNKTION ? null : neueFunktion);
  };

  /** "Funktion ändern" in einer Zeile; geteilt von der Ansprechpartner- und der vollen Zeile. */
  const aenderungsformular = (beziehung: RelationshipView, ziele: readonly string[]): ReactElement => (
    <form
      className={own.wahl}
      noValidate
      onSubmit={(event) => aendernSenden(event, beziehung)}
      onKeyDown={(event) => {
        // Ein Escape, das nur die offene Auswahlliste schliesst, laesst
        // die Zeile offen: Fluent verbraucht es dort bereits.
        const listeOffen = document.getElementById(funktionEingabeId(beziehung.relationshipId))?.getAttribute('aria-expanded') === 'true';
        if (event.key === 'Escape' && !event.defaultPrevented && !listeOffen) {
          event.preventDefault();
          aenderungSchliessen(beziehung);
        }
      }}
    >
      <Field
        label="Neue Funktion"
        hint="Die bisherige Angabe endet heute und bleibt in der Historie gespeichert."
        validationMessage={aenderungsfehler ?? null}
        validationState={aenderungsfehler === undefined ? 'none' : 'error'}
      >
        <Dropdown
          className={own.auswahlfeld}
          id={funktionEingabeId(beziehung.relationshipId)}
          disabled={busy}
          placeholder="Funktion wählen"
          value={neueFunktion === undefined ? '' : neueFunktion === OHNE_FUNKTION ? 'Keine Funktion' : (roleLabel(neueFunktion) ?? '')}
          selectedOptions={neueFunktion === undefined ? [] : [neueFunktion]}
          onOptionSelect={(_, data) => {
            setNeueFunktion(data.optionValue);
            if (aenderungsfehler !== undefined) setAenderungsfehler(undefined);
          }}
        >
          {ziele.map((code) => (
            <Option key={code} value={code}>
              {code === OHNE_FUNKTION ? 'Keine Funktion' : (roleLabel(code) ?? '')}
            </Option>
          ))}
        </Dropdown>
      </Field>
      <div className={styles.actions}>
        <Button appearance="secondary" type="submit" disabled={busy}>
          Funktion speichern
        </Button>
        <NebenKnopf disabled={busy} onClick={() => aenderungSchliessen(beziehung)}>
          Abbrechen
        </NebenKnopf>
      </div>
    </form>
  );

  /** Die Kanten dieses Teils: an einer Organisation geteilt, sonst alle. */
  const passend = (kanten: readonly RelationshipView[], istOrganisation: boolean): readonly RelationshipView[] => {
    if (teil === 'alle') return kanten;
    const gruppen = groupRelationships(kanten, istOrganisation);
    return teil === 'ansprechpartner' ? gruppen.contactPersons : gruppen.others;
  };

  /** Erste Seite plus nachgeladene, ohne Doppel. */
  const kantenVon = (bestand: Bestand): readonly RelationshipView[] => {
    if (weitere === undefined) return bestand.relationships;
    const gesehen = new Set(bestand.relationships.map((kante) => kante.relationshipId));
    return [...bestand.relationships, ...weitere.kanten.filter((kante) => !gesehen.has(kante.relationshipId))];
  };
  const mehrDa = (bestand: Bestand): boolean => (weitere === undefined ? bestand.hasMore : weitere.hasMore);

  /**
   * Blaettert ab dem Cursor, bis mindestens eine Kante DIESES Teils kam oder das
   * Ende erreicht ist: eine Seite kann nur Kanten des anderen Teils tragen, und
   * ein Klick ohne neuen Eintrag hiesse "da ist nichts" -- falsch, solange es
   * weitergeht (Muster kontakte-verbundene.tsx).
   */
  const weitereLaden = async (bestand: Bestand): Promise<void> => {
    const ab = weitere === undefined ? bestand.nextCursor : weitere.cursor;
    if (ab === undefined || nachladen.art === 'laedt') return;
    nachladeLauf.current += 1;
    const lauf = nachladeLauf.current;
    setNachladen({ art: 'laedt' });
    const istOrganisation = isOrganizationView(bestand.options);
    const bekannt = new Set(kantenVon(bestand).map((kante) => kante.relationshipId));
    const neu: RelationshipView[] = [];
    let cursor: string | undefined = ab;
    let hasMore = true;
    try {
      const token = await accessToken();
      if (lauf !== nachladeLauf.current) return;
      if (token === undefined) {
        setNachladen({ art: 'ruht' });
        onReauthenticate();
        return;
      }
      for (;;) {
        const seite = await fetchRelationshipPage({ baseUrl: apiBaseUrl, accessToken: token, partyId, ...(cursor === undefined ? {} : { cursor }) });
        if (lauf !== nachladeLauf.current) return;
        if (seite.outcome === 'not-found') {
          setNachladen({ art: 'ruht' });
          onReauthenticate();
          return;
        }
        if (seite.outcome === 'rejected') {
          setNachladen({ art: 'fehler', meldung: seite.message });
          return;
        }
        neu.push(...seite.relationships.filter((kante) => !bekannt.has(kante.relationshipId)));
        cursor = seite.nextCursor;
        hasMore = seite.hasMore;
        if (passend(neu, istOrganisation).length > 0 || cursor === undefined) break;
      }
    } catch {
      if (lauf === nachladeLauf.current) setNachladen({ art: 'fehler', meldung: LOAD_UNREACHABLE_MESSAGE });
      return;
    }
    const neuHier = passend(neu, istOrganisation);
    setWeitere((vorher) => ({ kanten: [...(vorher?.kanten ?? []), ...neu], cursor, hasMore }));
    setNachladen({ art: 'fertig', neu: neuHier.length });
    // Neue Ansprechpartner:innen stehen hinter den ersten fuenf: die Liste klappt auf.
    if (teil === 'ansprechpartner') setAlleZeigen(true);
    const erste = neuHier[0];
    setFokusAuf(erste !== undefined ? { id: erste.relationshipId } : 'status');
  };

  /** "Weitere laden" mit Statusregion am Knopf; ohne Cursor bleibt der Hinweis. */
  const weitereZeile = (bestand: Bestand): ReactElement => {
    const cursor = weitere === undefined ? bestand.nextCursor : weitere.cursor;
    return (
      <>
        {cursor !== undefined || nachladen.art !== 'ruht' ? (
          <div className={own.weitereZeile}>
            {cursor !== undefined ? (
              <Button
                appearance="secondary"
                size="small"
                className={own.kopfKnopf}
                aria-label={`Weitere laden: ${TITEL[teil]}`}
                disabledFocusable={nachladen.art === 'laedt'}
                onClick={() => void weitereLaden(bestand)}
              >
                Weitere laden
              </Button>
            ) : undefined}
            {/* Das Ergebnis AM KNOPF: bei langen Listen laege die Statusregion oben
                ausser Sicht, waehrend der Fokus unten steht. */}
            <div role="status" aria-live="polite" aria-label={`Nachladen: ${TITEL[teil]}`} ref={nachladeStatusRef} tabIndex={-1}>
              {nachladen.art === 'laedt' ? <Spinner size="tiny" labelPosition="after" label="Weitere werden geladen" /> : undefined}
              {nachladen.art === 'fertig' ? (
                <Caption1 className={own.hinweis}>
                  {nachladen.neu === 0 ? 'Keine weiteren.' : `${nachladen.neu} weitere.`}
                </Caption1>
              ) : undefined}
              {nachladen.art === 'fehler' ? (
                <Caption1>{satzfolge([nachladen.meldung.headline, nachladen.meldung.explanation, nachladen.meldung.nextStep])}</Caption1>
              ) : undefined}
            </div>
          </div>
        ) : undefined}
        {/* Die Vorgangsnummer ausserhalb der Live-Region (eigene Kopieransage). */}
        {nachladen.art === 'fehler' && nachladen.meldung.correlationId !== undefined ? (
          <Vorgang nummer={nachladen.meldung.correlationId} eingebettet />
        ) : undefined}
        {mehrDa(bestand) && cursor === undefined ? <Caption1 className={own.hinweis}>{HAS_MORE_NOTICE}</Caption1> : undefined}
      </>
    );
  };

  const zeile = (beziehung: RelationshipView): ReactElement => {
    const bezeichnung = roleLabel(beziehung.roleCode);
    const offen = inAenderung === beziehung.relationshipId;
    // Die bisherige Funktion steht nicht zur Wahl; "Keine Funktion" nur, wenn
    // es eine gibt. So kann die Auswahl nie "unveraendert" ergeben.
    const ziele = [
      ...(beziehung.roleCode === null ? [] : [OHNE_FUNKTION]),
      ...ROLE_CODES.filter((code) => code !== beziehung.roleCode),
    ];
    if (teil === 'ansprechpartner') {
      // v4 secAnsprech: EINE Zeile je Ansprechpartner -- Name, Funktion und "seit"
      // klein, die Aktionen im Menue "…" (DESIGN-P1 #518: drei Zeilen je Person
      // schoben die Hauptaktion unter die Kante). Die Eintraege tragen dieselben
      // Namen wie bisher die Knoepfe.
      return (
        <li key={beziehung.relationshipId} data-beziehung={beziehung.relationshipId}>
          <div className={own.ansprechZeile}>
            <div className={own.ansprechText}>
              <AppLink to={partyHref(beziehung.counterpartPartyId)}>{beziehung.counterpartDisplayName}</AppLink>{' '}
              <Caption1 className={own.wann}>
                {ansprechZeilentext(beziehung, bezeichnung)}
              </Caption1>
            </div>
            {nurLesen ? undefined : (
              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  <MehrKnopf id={funktionKnopfId(beziehung.relationshipId)} appearance="subtle" size="small" className={own.ansprechMenue} disabled={busy} bezug={`Aktionen für ${beziehung.counterpartDisplayName}`} icon={<MehrSymbol />} />
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    {typeCarriesRole(beziehung.relationshipTypeCode) ? (
                      <MenuItem aria-label={changeRoleButtonLabel(beziehung)} onClick={() => aenderungOeffnen(beziehung)}>
                        Funktion ändern
                      </MenuItem>
                    ) : undefined}
                    <MenuItem aria-label={`Beziehung „${relationshipLine(beziehung)}" beenden`} onClick={() => beenden(beziehung)}>
                      Beenden
                    </MenuItem>
                  </MenuList>
                </MenuPopover>
              </Menu>
            )}
          </div>
          {offen ? aenderungsformular(beziehung, ziele) : undefined}
        </li>
      );
    }
    return (
      <li key={beziehung.relationshipId} className={own.eintrag} data-beziehung={beziehung.relationshipId}>
        <Body1 className={own.text}>
          {beziehung.reading} <AppLink to={partyHref(beziehung.counterpartPartyId)}>{beziehung.counterpartDisplayName}</AppLink>
        </Body1>
        {bezeichnung !== undefined ? <Caption1 className={own.wert}>Funktion: {bezeichnung}</Caption1> : undefined}
        <Caption1 className={own.wert}>{sinceText(formatSinceMonth(beziehung.validFrom))}</Caption1>
        {/* Ohne Rueckfrage (ADR-0061 Ziffer 17); der Name traegt den vollen
            Satz, sonst hoerte man "Beenden" fuenfzigmal. */}
        {nurLesen ? undefined : (
        <Zeilenaktionen sofort name={`Beziehung „${relationshipLine(beziehung)}"`} busy={busy} onBeenden={() => beenden(beziehung)}>
          {typeCarriesRole(beziehung.relationshipTypeCode) && !offen ? (
            <Button
              id={funktionKnopfId(beziehung.relationshipId)}
              appearance="subtle"
              disabled={busy}
              aria-label={changeRoleButtonLabel(beziehung)}
              onClick={() => aenderungOeffnen(beziehung)}
            >
              Funktion ändern
            </Button>
          ) : undefined}
        </Zeilenaktionen>
        )}
        {offen ? aenderungsformular(beziehung, ziele) : undefined}
      </li>
    );
  };

  /** Oeffner und Anlegeformular; geteilt von allen Teilen (eigene Kennungen je Teil). */
  const oeffnerUndFormular = (): ReactElement => (
    <>
      {lesarten.length === 0 ? (
        <Caption1 className={own.hinweis}>Für diesen Kontakt lässt sich hier keine Art von Beziehung festhalten.</Caption1>
      ) : undefined}

      {lesarten.length > 0 && formOffen && !nurLesen ? (
        <div role="group" aria-label={teil === 'ansprechpartner' ? 'Bestehenden Kontakt zuordnen' : 'Beziehung hinzufügen'}>
          <form className={own.wahl} noValidate onSubmit={absenden} onKeyDown={(event) => beiEscapeSchliessen(event, formSchliessen)}>
            {/* Der Satz zur Sichtbarkeit steht im Formular (v4, Ü-6): dort wird er gebraucht. */}
            <Caption1 className={own.hinweis}>Eine Beziehung sehen alle, die beide Kontakte öffnen können.</Caption1>
            <Field
              label="Art der Beziehung"
              hint={'Aus Sicht dieses Kontakts, zum Beispiel „arbeitet für". Den Beginn setzt LIAISON.'}
              validationMessage={lesartFehler ?? null}
              validationState={lesartFehler === undefined ? 'none' : 'error'}
            >
              <Dropdown
                className={own.auswahlfeld}
                id={ids.lesart}
                disabled={busy}
                placeholder="Art wählen"
                value={gewaehlteLesart === undefined ? '' : gewaehlteLesart.reading}
                selectedOptions={gewaehlteLesart === undefined ? [] : [optionKey(gewaehlteLesart)]}
                onOptionSelect={(_, data) => {
                  setLesart(data.optionValue);
                  if (lesartFehler !== undefined) setLesartFehler(undefined);
                  // Passt der gewaehlte Treffer nicht mehr zur neuen Lesart,
                  // faellt er weg -- die Liste filtert sich dann neu.
                  const naechste = lesarten.find((option) => optionKey(option) === data.optionValue);
                  const bleibt = treffer === undefined ? [] : eligibleCounterparts(treffer, naechste, partyId);
                  if (gegenpartei !== undefined && !bleibt.some((eintrag) => eintrag.partyId === gegenpartei)) {
                    setGegenpartei(undefined);
                  }
                  // Eine Funktion gibt es nur an "arbeitet für" und "arbeitet frei für".
                  if (naechste === undefined || !typeCarriesRole(naechste.relationshipTypeCode)) {
                    setFunktion(undefined);
                  }
                }}
              >
                {lesarten.map((option) => (
                  <Option key={optionKey(option)} value={optionKey(option)}>
                    {option.reading}
                  </Option>
                ))}
              </Dropdown>
            </Field>

            <Field
              label="Anderer Kontakt"
              hint="Suche nach Name oder Kennung, mindestens drei Zeichen."
              validationMessage={sucheFehler ?? null}
              validationState={sucheFehler === undefined ? 'none' : 'error'}
            >
              <Input
                className={own.auswahlfeld}
                id={ids.suche}
                type="search"
                value={begriff}
                disabled={busy}
                onChange={(_, data) => {
                  setBegriff(data.value);
                  if (sucheFehler !== undefined) setSucheFehler(undefined);
                }}
                onKeyDown={(event) => {
                  // Enter sucht, statt das Formular abzuschicken: im Suchfeld
                  // ist "Suchen" die naheliegende Handlung.
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void suchen();
                  }
                }}
              />
            </Field>
            <div className={styles.actions}>
              <Button appearance="outline" disabled={busy || sucht} onClick={() => void suchen()}>
                Kontakt suchen
              </Button>
            </div>

            {passende.length > 0 ? (
              <Field
                label="Treffer"
                hint={gewaehlteLesart === undefined ? 'Wähle oben die Art, dann bleiben nur die passenden Kontakte.' : null}
                validationMessage={gegenparteiFehler ?? null}
                validationState={gegenparteiFehler === undefined ? 'none' : 'error'}
              >
                {/* Eine Auswahlgruppe: Tab fuehrt hinein, die Pfeiltasten
                    waehlen, Enter haelt fest. */}
                <RadioGroup
                  id={ids.treffer}
                  value={gegenpartei ?? ''}
                  disabled={busy}
                  onChange={(_, data) => {
                    setGegenpartei(data.value);
                    if (gegenparteiFehler !== undefined) setGegenparteiFehler(undefined);
                  }}
                >
                  {passende.map((eintrag) => (
                    <Radio key={eintrag.partyId} value={eintrag.partyId} label={`${eintrag.displayName} (${kindLabel(eintrag.kind)})`} />
                  ))}
                </RadioGroup>
              </Field>
            ) : undefined}
            {trefferMehr && passende.length > 0 ? (
              <Caption1 className={own.hinweis}>Es gibt weitere Treffer. Gib mehr vom Namen ein, um genauer zu suchen.</Caption1>
            ) : undefined}

            {/* Progressive Offenlegung: das Feld erscheint erst, wenn die
                gewaehlte Art eine Funktion traegt, und bleibt freiwillig. Es steht
                hinter dem anderen Kontakt: "arbeitet für X, als Y". */}
            {gewaehlteLesart !== undefined && typeCarriesRole(gewaehlteLesart.relationshipTypeCode) ? (
              <Field label="Funktion (freiwillig)" hint="Wofür der Kontakt dort zuständig ist, zum Beispiel Presse oder Einkauf.">
                <Dropdown
                  className={own.auswahlfeld}
                  id={ids.funktion}
                  disabled={busy}
                  placeholder="Keine Funktion"
                  value={funktion === undefined ? '' : (roleLabel(funktion) ?? '')}
                  selectedOptions={funktion === undefined ? [] : [funktion]}
                  onOptionSelect={(_, data) => setFunktion(data.optionValue === OHNE_FUNKTION ? undefined : data.optionValue)}
                >
                  <Option value={OHNE_FUNKTION}>Keine Funktion</Option>
                  {ROLE_CODES.map((code) => (
                    <Option key={code} value={code}>
                      {roleLabel(code) ?? ''}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
            ) : undefined}

            <div className={styles.actions}>
              {/* secondary UND NICHT primary: die EINE Hauptaktion des
                  Bildschirms bleibt "Notiz festhalten" (NFR-UX-004). */}
              <Button appearance="secondary" type="submit" disabled={busy}>
                Beziehung festhalten
              </Button>
              <NebenKnopf
                disabled={busy}
                onClick={formSchliessen}
              >
                Abbrechen
              </NebenKnopf>
            </div>
          </form>
        </div>
      ) : undefined}
    </>
  );

  // "+ Beziehung" am Ende der Ueberschrift (v4 "+ Stichwort", DESIGN 13.10.): sichtbar
  // kurz, der Name beginnt mit dem sichtbaren Wort (WCAG 2.5.3). Der Oeffner
  // verschwindet, solange das Formular offen ist (Bauvorgabe 4c).
  const plusBeziehung =
    teil !== 'ansprechpartner' && lesarten.length > 0 && !formOffen && !nurLesen ? (
      <Button
        id={ids.oeffner}
        appearance="subtle"
        size="small"
        className={own.abschnittPlus}
        icon={<PlusSymbol />}
        aria-label="Beziehung hinzufügen"
        onClick={() => zuordnenOeffnen(ids.oeffner)}
      >
        Beziehung
      </Button>
    ) : undefined;

  return (
    <section className={own.gruppe} aria-labelledby={ids.ueberschrift}>
      {eingebettet ? plusBeziehung : (
        <div className={own.abschnittKopf} data-abschnittkopf="">
          <Subtitle1 as="h2" id={ids.ueberschrift}>
            {TITEL[teil]}
            {/* v4: die Zahl still neben dem Titel -- nur, wenn die Liste vollstaendig
                ist; sonst verriete sie nichts Wahres. Vorgelesen nennt die Liste sie. */}
            {teil === 'ansprechpartner' && anzahl !== undefined ? (
              <span className={own.kopfZahl} aria-hidden="true">
                {anzahl}
              </span>
            ) : undefined}
          </Subtitle1>
          {plusBeziehung}
          {/* Eine neue Person als Ansprechpartner: die Schnellerfassung mit
              vorbelegtem "arbeitet für" (Slice 6b; die Vorbelegung bleibt im
              Speicher des Tabs, nie in der Adresse). */}
          {/* "Anlegen" und "…" bilden eine Gruppe, die nicht in sich umbricht: bei
              390 px stuende das "…" sonst allein links unter der h2 (DESIGN-P2 #530). */}
          {teil === 'ansprechpartner' && !nurLesen ? (
            <span className={own.kopfGruppe}>
              {/* Sichtbar kurz, damit die Kopfzeile bei 1024 px einzeilig bleibt (sonst
                  rutscht die Hauptaktion unter die Kante); Name und Tooltip tragen den
                  Bezug und beginnen mit dem sichtbaren Wort (DESIGN 13.10., WCAG 2.5.3). */}
              {organisation !== undefined ? (
                <Tooltip content="Anlegen: Ansprechpartner:in" relationship="label">
                  <Button
                    appearance="secondary"
                    size="small"
                    className={own.kopfKnopf}
                    icon={<PlusSymbol />}
                    onClick={() => {
                      presetWorksFor(organisation);
                      navigate('/kontakt-anlegen');
                    }}
                  >
                    Anlegen
                  </Button>
                </Tooltip>
              ) : undefined}
              {/* Das Zuordnen steht im Menue "…" daneben: v4 kennt in der Kopfzeile nur
                  das Anlegen, und mit zwei Knoepfen brach sie bei 1024 px in zwei
                  Zeilen und schob die Hauptaktion unter die Kante (DESIGN-P3 #518). */}
              {lesarten.length > 0 && !formOffen ? (
                <Menu>
                  <MenuTrigger disableButtonEnhancement>
                    <MehrKnopf
                      id={ids.oeffner}
                      appearance="subtle"
                      size="small"
                      className={own.ansprechMenue}
                      bezug="Weitere Wege zu Ansprechpartner:innen"
                      icon={<MehrSymbol />}
                    />
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      <MenuItem onClick={() => zuordnenOeffnen(ids.oeffner)}>Bestehenden Kontakt zuordnen</MenuItem>
                    </MenuList>
                  </MenuPopover>
                </Menu>
              ) : undefined}
            </span>
          ) : undefined}
        </div>
      )}

      {/* Wer nichts festhalten kann, sieht kein Formular: dort steht der Satz zur
          Sichtbarkeit dann als Beschreibung (B73, der beschreibende Rest bleibt). */}
      {nurLesen && teil !== 'ansprechpartner' ? <Caption1 className={own.hinweis}>Eine Beziehung sehen alle, die beide Kontakte öffnen können.</Caption1> : undefined}

      <Pflegeabschnitt
        abschnitt={abschnitt}
        partyId={partyId}
        texte={TEXTE}
        eingebettet={{ name: TITEL[teil], statusId: ids.status }}
        onErneutAnlegen={festhalten}
        zusatzmeldung={
          <>
            {sucht ? <Caption1 className={own.hinweis}>Suche läuft.</Caption1> : undefined}
            {suchsatz !== undefined ? <Caption1 className={own.hinweis}>{suchsatz}</Caption1> : undefined}
          </>
        }
      >
        {(bestand) => {
          const istOrganisation = isOrganizationView(bestand.options);
          const kanten = kantenVon(bestand);
          const gruppen = groupRelationships(kanten, istOrganisation);
          // Ein Teil (Organisation, Slice 6b): nur seine Kanten, ohne Unterueberschriften.
          const teilListe = teil === 'ansprechpartner' ? gruppen.contactPersons : teil === 'weitere' ? gruppen.others : undefined;
          if (teilListe !== undefined) {
            return (
              <>
                {teil === 'ansprechpartner' ? (
                  <AnzahlMelden anzahl={mehrDa(bestand) || teilListe.length === 0 ? undefined : teilListe.length} melden={setAnzahl} />
                ) : undefined}
                {teilListe.length === 0 ? (
                  // Ist die Liste gekuerzt, ist "keine" nicht belegt: dann sagt der Satz
                  // nur, dass unter den gezeigten keine dieser Art ist (Codex-Cloud-P2 #518).
                  <Caption1 className={own.hinweis}>
                    {mehrDa(bestand)
                      ? teil === 'ansprechpartner'
                        ? 'Unter den neuesten Beziehungen sind keine Ansprechpartner:innen.'
                        : 'Unter den neuesten Beziehungen ist keine weitere.'
                      : teil === 'ansprechpartner'
                        ? NO_CONTACT_PERSONS_STATEMENT
                        : NO_FURTHER_RELATIONSHIPS}
                  </Caption1>
                ) : (
                  <ul
                    ref={listeRef}
                    className={own.liste}
                    aria-label={teil === 'ansprechpartner' ? 'Ansprechpartner:innen dieser Organisation' : 'Beziehungen dieser Organisation'}
                  >
                    {(teil === 'ansprechpartner' && !alleZeigen ? teilListe.slice(0, SICHTBARE_ANSPRECHPARTNER) : teilListe).map(zeile)}
                  </ul>
                )}
                {teilListe.length === 0 && teil === 'ansprechpartner' && lesarten.length > 0 && !formOffen && !nurLesen ? (
                  // Ohne Ansprechpartner sucht man gerade das Zuordnen; Platz ist da
                  // (DESIGN-P2 #530). Mit Eintraegen bleibt es im Menue "…".
                  <div>
                  <Button
                    id={`${ids.oeffner}-leer`}
                    appearance="subtle"
                    size="small"
                    className={own.abschnittPlus}
                    icon={<VerknuepfenSymbol />}
                    onClick={() => zuordnenOeffnen(`${ids.oeffner}-leer`)}
                  >
                    Bestehenden Kontakt zuordnen
                  </Button>
                  </div>
                ) : undefined}
                {teil === 'ansprechpartner' && !alleZeigen && teilListe.length > SICHTBARE_ANSPRECHPARTNER ? (
                  <Button
                    appearance="subtle"
                    onClick={() => {
                      setAlleZeigen(true);
                      setFokusSechste(true);
                    }}
                  >
                    {`Alle ${teilListe.length} Ansprechpartner:innen anzeigen`}
                  </Button>
                ) : undefined}
                {weitereZeile(bestand)}
                {oeffnerUndFormular()}
              </>
            );
          }
          return (
            <>
              {/* Der Bestand -- AUSSERHALB der Statusregion. */}
              {kanten.length === 0 ? <Caption1 className={own.hinweis}>{EMPTY_STATEMENT}</Caption1> : undefined}

              {/* An einer Organisation stehen die Menschen zuerst: vor dem Anruf
                  sucht man, wer dort fuer was zustaendig ist. */}
              {gruppen.contactPersons.length > 0 ? (
                <>
                  <Subtitle2 as="h3" className={own.wert}>
                    Ansprechpartner:innen
                  </Subtitle2>
                  <ul className={own.liste} aria-label="Ansprechpartner:innen dieser Organisation">
                    {gruppen.contactPersons.map(zeile)}
                  </ul>
                </>
              ) : undefined}

              {istOrganisation && kanten.length > 0 && gruppen.contactPersons.length === 0 ? (
                <Caption1 className={own.hinweis}>{NO_CONTACT_PERSONS_STATEMENT}</Caption1>
              ) : undefined}

              {gruppen.others.length > 0 ? (
                <>
                  {gruppen.contactPersons.length > 0 ? (
                    <Subtitle2 as="h3" className={own.wert}>
                      Weitere Beziehungen
                    </Subtitle2>
                  ) : undefined}
                  <ul className={own.liste} aria-label={gruppen.contactPersons.length > 0 ? 'Weitere Beziehungen dieser Organisation' : 'Beziehungen dieses Kontakts'}>
                    {gruppen.others.map(zeile)}
                  </ul>
                </>
              ) : undefined}

              {weitereZeile(bestand)}

              {oeffnerUndFormular()}
            </>
          );
        }}
      </Pflegeabschnitt>
    </section>
  );
}
